import type { QueryClient } from '@tanstack/react-query'
import type { Workspaces } from '@scc/types'
import { authRepository } from '@/data/adapters/api/auth.api'

/** SAME key the `$orgSlug` and `$clientSlug` guards share, so they reuse one
 * cached fetch across a single org -> client navigation. */
export function workspacesQueryKey() {
  return ['auth', 'workspaces'] as const
}

/**
 * Cache-first read of the caller's workspaces (organizations + direct
 * stores), for the common case where the cached list already has the answer.
 *
 * Mirrors `resolveClientBySlug` in `resolve-client.ts`: callers here MUST
 * pair this with `refetchWorkspaces` on a miss rather than treating "not in
 * the cached list" as proof of no access — see that file's docstring for the
 * false-404-on-stale-list rationale (a store granted, or created, after the
 * cache was filled reads as absent until something forces a real fetch).
 */
export async function ensureWorkspaces(queryClient: QueryClient): Promise<Workspaces> {
  return queryClient.ensureQueryData({
    queryKey: workspacesQueryKey(),
    queryFn: () => authRepository.getWorkspaces(),
  })
}

/**
 * Forces one authoritative (bypass-cache) read of the caller's workspaces.
 *
 * Only call this after `ensureWorkspaces` misses — the fast path stays
 * cache-first and free; this pays a request only to avoid a false 404 (see
 * `ensureWorkspaces`'s docstring). Also refreshes the shared cache, so any
 * other consumer keyed on `workspacesQueryKey()` stops lagging too.
 */
export async function refetchWorkspaces(queryClient: QueryClient): Promise<Workspaces> {
  return queryClient.fetchQuery({
    queryKey: workspacesQueryKey(),
    queryFn: () => authRepository.getWorkspaces(),
    staleTime: 0,
  })
}
